import { Field, useFormikContext } from "formik";
import React, { FC } from "react";
import Label from "../../../components/label/label";
import Togglebox from "../../../components/togglebox/togglebox";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { validateIntegerWithinBounds, validateItemName } from "../../../utilities/Validation";

interface EmailAccountAdvProps {
    testAccount: () => void
}

export const EmailAccountAdvanced: FC<EmailAccountAdvProps> = ({testAccount}) => {
    const { translate } = useLocale();
    const { errors,values, isValid, dirty } = useFormikContext<any>();

    const enableButton = (): boolean => {
        const validCheck = values.id && isValid && !dirty;
        return validCheck;
    };

    return (
        <div>
            {/*row 1 start (Username, Pwd) */}
            <div className="flex w-full space-x-20 px-8">
                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="userName"
                    >
                        <Label
                            htmlFor="userName"
                            className="text-xs text-gray-600 mt-1"
                            required
                        >
                            {translate.byKey(TranslationKeys.username)}:
                        </Label>
                            {errors.username ? <span className='text-red-600 text-xs'>{errors.username['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            name="username.label"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => validateItemName(value, translate)}
                            id="usernameInputId"
                            style={{ width: "230px" }}
                        />
                    </div>
                </div>

                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="password"
                    >
                        <Label
                            htmlFor="password"
                            className="text-xs text-gray-600 mt-1"
                        >
                            {translate.byKey(TranslationKeys.password)}:
                        </Label>
                            {errors.password ? <span className='text-red-600 text-xs'>{errors.password['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="password"
                            name="password.label"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            id="passwordInputId"
                            style={{ width: "230px" }}
                            placeholder={(values?.isPasswordCaptured) ? "********" : ""}
                        />
                    </div>
                </div>
            </div>
            {/* row 1 end */}

            {/* row 2 start (Outgoing server & port) */}
            <div className="flex w-full space-x-20 px-8">
                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="outgoingServer.label"
                    >
                        <Label
                            htmlFor="outgoingServer.label"
                            className="text-xs text-gray-600 mt-1"
                            required
                        >
                            {translate.byKey(TranslationKeys.outgoingServer)}:
                        </Label>
                            {errors && errors.outgoingServer ? <span className='text-red-600 text-xs'>{errors.outgoingServer['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            name="outgoingServer.label"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            id="outgoingServerInputId"
                            validate={(value) => validateItemName(value, translate)}
                            style={{ width: "230px" }}
                        />
                    </div>
                </div>

                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="outgoingPort.label"
                    >
                        <Label
                            htmlFor="outgoingPort.label"
                            className="text-xs text-gray-600 mt-1"
                            required
                        >
                            {translate.byKey(TranslationKeys.outgoingPort)}:
                        </Label>
                            {errors.outgoingPort ? <span className='text-red-600 text-xs'>{errors.outgoingPort['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="number"
                            name="outgoingPort.label"
                            min={1}
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={() => validateIntegerWithinBounds(values.outgoingPort.label, 1, 65535, translate)}
                            id="outgoingPortInputId"
                            style={{ width: "230px" }}
                        />
                    </div>
                </div>
            </div>
            {/* row 2 end */}

            {/* row 3 start (Incoming server & port) */}
            <div className="flex w-full space-x-20 px-8">
                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="incomingServer.label"
                    >
                        <Label
                            htmlFor="incomingServer.label"
                            className="text-xs text-gray-600 mt-1"
                        >
                            {translate.byKey(TranslationKeys.incomingServer)}:
                        </Label>
                            {errors.incomingServer ? <span className='text-red-600 text-xs'>{errors.incomingServer['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            name="incomingServer.label"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            id="incomingServerInputId"
                            style={{ width: "230px" }}
                        />
                    </div>
                </div>

                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between"
                        id="incomingPort.label"
                    >
                        <Label
                            htmlFor="incomingPort.label"
                            className="text-xs text-gray-600 mt-1"
                            required
                        >
                            {translate.byKey(TranslationKeys.incomingPort)}:
                        </Label>
                            {errors.incomingPort ? <span className='text-red-600 text-xs'>{errors.incomingPort['label']}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="number"
                            name="incomingPort.label"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={() => validateIntegerWithinBounds(values.incomingPort.label, 1, 65535, translate)}
                            id="incomingPortInputId"
                            style={{ width: "230px" }}
                            min={1}
                        />
                    </div>
                </div>
            </div>
            {/* row 3 end */}

            {/* row 4 start (Authentication required) */}
            <div className="flex w-full space-x-20 px-8">
                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between mb-1"
                        id="auth.label"
                    >
                        <Label
                            htmlFor="auth.label"
                            className="text-xs text-gray-600 mt-1"
                        >
                            {translate.byKey(TranslationKeys.authenticationRequired)}:
                        </Label>
                    </div>
                    <div className="flex flex-row justify-between" id="authTogglebox">
                        <Togglebox name="auth.label" label={""} />
                    </div>
                </div>
            </div>
            {/* row 4 end */}

            {/* row 5 start (TLS) */}
            <div className="flex w-full space-x-20 px-8">
                <div className="mb-2">
                    <div
                        className="flex flex-row justify-between mb-1"
                        id="ssl.label"
                    >
                        <Label
                            htmlFor="ssl.label"
                            className="text-xs text-gray-600 mt-1"
                        >
                            {translate.byKey(TranslationKeys.secureConnectionTLS)}:
                        </Label>
                    </div>
                    <div className="flex flex-row justify-between" id="sslTogglebox">
                        <Togglebox name="ssl.label" label={""} />
                    </div>
                </div>
            </div>
            {/* row 5 end */}

            <div className="flex w-full space-x-20 px-8">
                <button
                    type="button" 
                    className={enableButton()
                        ? " h-8 px-2  mt-3 secondary secondary-enabled text-white rounded-lg shadow-lg" 
                        : " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2  mt-3" }
                    disabled ={!enableButton()}
                    onClick={(e) => testAccount()}
                    id="testEmailSettingsButton"
                >
                    <span className="mr-2">{translate.byKey(TranslationKeys.testEmailSettings)}</span>
                </button>
            </div>
        </div>
    );
};
